/**
 * Картинки банды: аватар (квадрат до 256 px) и фон (до 500 px по длинной стороне).
 *
 * Контракт совпадает с фоном профиля, поэтому подготовка фона переиспользуется
 * из features/profile/utils/profileBackground.js — дублировать canvas-логику не нужно.
 */

export {
  BG_DEFAULTS,
  BG_FITS,
  backgroundImageStyle,
  backgroundOverlayStyle,
  buildBackgroundPayload,
  fromBackground,
  prepareBackgroundFile
} from '../../profile/utils/profileBackground'

// Лимиты аватара из контракта: квадрат до 256×256, JPG/PNG, до 1 МБ
export const AVATAR_MAX_SIDE = 256
export const AVATAR_MAX_BYTES = 1024 * 1024

/**
 * Подготовка аватара: центральный квадрат картинки, уменьшенный до 256 px.
 * Бэкенд обрезает центр сам, но клиентская обрезка предсказуемее для
 * пользователя — он видит ровно то, что загрузил. Картинка меньше 256 px не
 * растягивается (из фото 100×80 получится квадрат 80×80).
 * @throws {{code: string}} invalid_image — не декодируется; file_too_large — не влезает в 1 МБ
 */
export const prepareAvatarFile = (file) => new Promise((resolve, reject) => {
  const isPng = file.type === 'image/png'

  const url = URL.createObjectURL(file)
  const img = new Image()

  img.onload = () => {
    URL.revokeObjectURL(url)

    const side = Math.min(img.naturalWidth, img.naturalHeight)
    if (!side) {
      reject({ code: 'invalid_image' })
      return
    }

    // Меньшая сторона не растягивается — берём её же, но не больше 256
    const target = Math.min(side, AVATAR_MAX_SIDE)
    const canvas = document.createElement('canvas')
    canvas.width = target
    canvas.height = target
    canvas.getContext('2d').drawImage(
      img,
      Math.round((img.naturalWidth - side) / 2),
      Math.round((img.naturalHeight - side) / 2),
      side,
      side,
      0,
      0,
      target,
      target
    )

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject({ code: 'invalid_image' })
          return
        }
        if (blob.size > AVATAR_MAX_BYTES) {
          reject({ code: 'file_too_large' })
          return
        }
        // canvas не сохраняет имя файла — задаём сами по фактическому формату
        resolve({ blob, name: isPng ? 'avatar.png' : 'avatar.jpg' })
      },
      isPng ? 'image/png' : 'image/jpeg',
      isPng ? undefined : 0.92
    )
  }

  img.onerror = () => {
    URL.revokeObjectURL(url)
    reject({ code: 'invalid_image' })
  }

  img.src = url
})
